/**
 * Rich Text Resolver
 *
 * Resolves embedded field tokens in richtext HTML:
 *   <span data-type="field-token" data-field="property_name"></span>
 *   → "Sunset Apartments"
 *
 * Also resolves dynamic link targets:
 *   <a data-field-href="property_apply_now_url">Apply Now</a>
 *   → <a href="https://apply.example.com">Apply Now</a>
 *
 *   <a data-field-href="property_phone" data-field-href-type="tel">Call</a>
 *   → <a href="tel:+15551234567">Call</a>
 *
 * Safe to call on any HTML — if no data-field spans are found, it returns
 * the input unchanged (no-op for legacy richtext).
 */

// Matches any <span ...></span> (empty or whitespace-only) regardless of attributes/ordering
const SPAN_RE = /<span\b([^>]*)>\s*<\/span>/g;

// Matches <a ...> opening tags that contain data-field-href
const ANCHOR_RE = /<a\b([^>]*)>/g;

// Matches a single empty <p> — empty in the broad sense: any attributes, and
// inner content that is purely whitespace, &nbsp;, or <br> filler (which is
// what ProseMirror/Tiptap emits for a paragraph holding only a hardBreak, and
// what other WYSIWYGs commonly produce on pasted/imported HTML).
const EMPTY_P = String.raw`<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>`;
const LEADING_EMPTY_P_RE = new RegExp(`^(?:\\s*${EMPTY_P}\\s*)+`, 'i');
const TRAILING_EMPTY_P_RE = new RegExp(`(?:\\s*${EMPTY_P}\\s*)+$`, 'i');

/**
 * Remove leading/trailing empty paragraphs from a richtext HTML string.
 *
 * Edge blanks are nearly always accidents from copy/paste, a trailing Enter, or
 * Shift+Enter at the end of an edit session. Internal blank "spacer" paragraphs
 * are handled separately at render by `collapseBlankParagraphs` in
 * `react/rich-text-content.tsx` (paragraph spacing is owned by the richtext base
 * CSS margins now), so authors don't need blank lines to add a gap.
 *
 * Tolerant of `<p><br></p>`, `<p>&nbsp;</p>`, and paragraphs with attributes;
 * those are all forms of "visually empty" that show up in stored richtext.
 */
export function stripEdgeEmptyParagraphs(html: string): string {
  if (!html) return html;
  return html.replace(LEADING_EMPTY_P_RE, '').replace(TRAILING_EMPTY_P_RE, '').trim();
}

/**
 * HTML-escape a string for safe insertion into HTML content.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Get a string value from a property object, coercing primitives.
 */
function getStringValue(property: Record<string, any>, field: string): string {
  const val = property[field];
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  return '';
}

/**
 * Extract a data-field name from a span's attributes if it is a field-token.
 * Handles any attribute ordering, extra attributes (style, class, etc.), and whitespace.
 */
function extractFieldTokenName(attrs: string): string | null {
  if (!/data-type\s*=\s*"field-token"/.test(attrs)) return null;
  const m = attrs.match(/data-field\s*=\s*"([^"]+)"/);
  return m ? m[1] : null;
}

/**
 * Build a final href from a resolved field value and its href-type marker.
 * - 'tel'    → strip everything except digits and a single leading `+`
 *              (E.164-friendly; browsers reliably parse this form).
 * - 'mailto' → trim whitespace; the value is already an email.
 * - 'url'/null → use the value as-is (current default behavior).
 */
function buildHrefForType(value: string, type: string | null): string {
  if (!value) return '';
  if (type === 'tel') {
    const hasPlus = /^\s*\+/.test(value);
    const digits = value.replace(/\D+/g, '');
    if (!digits) return '';
    return `tel:${hasPlus ? '+' : ''}${digits}`;
  }
  if (type === 'mailto') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    return `mailto:${trimmed}`;
  }
  return value;
}

/**
 * Resolve all embedded field tokens and dynamic links in a richtext HTML string.
 *
 * - Field tokens: `<span data-type="field-token" data-field="X">` → escaped property value
 * - Dynamic links: `<a data-field-href="X">` → `<a href="resolved_value">`
 *
 * Returns the HTML unchanged if no field tokens or dynamic links are found.
 */
export function resolveRichText(html: string, property: Record<string, any>): string {
  if (!html) return '';

  let result = html;

  // Resolve field tokens — single pass, attribute-order-agnostic
  result = result.replace(SPAN_RE, (full, attrs) => {
    const field = extractFieldTokenName(attrs);
    if (!field) return full;
    return escapeHtml(getStringValue(property, field));
  });

  // Resolve dynamic links: replace data-field-href with resolved href, preserve other attrs.
  // data-field-href-type opts the value into a tel:/mailto: wrapper before serialization.
  result = result.replace(ANCHOR_RE, (full, attrs) => {
    const hrefMatch = attrs.match(/data-field-href\s*=\s*"([^"]+)"/);
    if (!hrefMatch) return full;

    const field = hrefMatch[1];
    const typeMatch = attrs.match(/data-field-href-type\s*=\s*"([^"]+)"/);
    const hrefType = typeMatch ? typeMatch[1] : null;
    const resolvedValue = getStringValue(property, field);
    const finalHref = buildHrefForType(resolvedValue, hrefType);
    const cleanedAttrs = attrs
      .replace(/\s*data-field-href-type\s*=\s*"[^"]*"/, '')
      .replace(/\s*data-field-href\s*=\s*"[^"]*"/, '');
    const href = finalHref ? ` href="${escapeHtml(finalHref)}"` : '';
    return `<a${href}${cleanedAttrs}>`;
  });

  return result;
}
